/**
 * #1251 — the CONTROL PLANE's own pods, and one of their container logs.
 *
 * | endpoint | producer | permission | gateway |
 * |---|---|---|---|
 * | `GET /v1/admin/control-plane/pods` | `site-admin-api/src/modules/observability/cp-pods.controller.ts` (`listPods`) | `cp:control-plane:pod-log:read` | site-admin-api |
 * | `GET /v1/admin/control-plane/pods/:podName/logs` | same file (`readPodLog`) | `cp:control-plane:pod-log:read` | site-admin-api |
 *
 * ## ⛔ ONE PERMISSION GATES BOTH, so there is no partial state
 *
 * R-27 ruled a single string rather than a weaker one for the list and a stronger one for the
 * bytes. The list exists only to feed the log reader, so a principal either sees the whole screen
 * or none of it — never "can enumerate the pods, cannot read the bytes". ⛔ Do not add a second
 * permission constant here "for symmetry": a gate no fixture can fail is a gate that rots.
 *
 * ## ⛔ THERE IS NO LIVE TAIL, AND THIS MODULE MUST NOT GROW ONE
 *
 * No `follow`, no `EventSource`, no interval. The producer's query schema is `.strict()` so
 * `?follow=true` is a 400 — **but the 400 is not the reason.** The reason is the honesty rule
 * `appLogs.api.ts:1-25` states: *"a 5-second poll that looks like a tail but drops lines between
 * requests is worse than an honest refresh button, because the gap is invisible."*
 *
 * ## ⛔ THE NAMESPACE IS NOT A PARAMETER HERE, AND THAT IS DELIBERATE
 *
 * The server confines every read to `CP_NAMESPACE` (`metadata.namespace` via the downward API) and
 * the k8s Role is namespaced to that same namespace. There is no client-side namespace to send, and
 * adding one would be a 400 from `.strict()`. The response ECHOES the namespace it read so the
 * console never has to guess it.
 */
import type { AppPodLogResponse, CpPodsResponse } from '@zion/sdk-contracts';
import { DEFAULT_TAIL_LINES, MAX_CP_PODS, MAX_TAIL_LINES } from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

export type { AppPodLogResponse, CpPodsResponse };
export { DEFAULT_TAIL_LINES, MAX_CP_PODS, MAX_TAIL_LINES };

/**
 * ⛔ TRANSCRIBED from `cp-pods.controller.ts`'s two `@RequirePermission` decorators, not invented.
 *
 * An invented permission string inside a manifest `anyOf` **silently never matches** — the gate
 * closes and the screen simply never appears, with no error anywhere (`dlq.api.ts:20-26`). That is
 * why the literal is asserted in `cp-pods-api-paths.test.ts` rather than merely written here.
 */
export const CP_POD_LOG_READ_PERMISSION = 'cp:control-plane:pod-log:read';

export const CP_PODS_PATH = '/v1/admin/control-plane/pods';

export function cpPodsPath(): string {
  return CP_PODS_PATH;
}

/** Every pod in the control plane's own namespace, with derived `problems`. */
export function listCpPods(): Promise<CpPodsResponse> {
  return api.get<CpPodsResponse>(cpPodsPath());
}

export interface CpPodLogQueryInput {
  readonly container?: string;
  /**
   * ⛔ The STRING enum, not a boolean.
   *
   * The contract carries `'true' | 'false'` because `z.coerce.boolean()` maps the string `'false'`
   * to `true` — every non-empty string is truthy — so a coerced contract would serve the PREVIOUS
   * container instance for `?previous=false`, the exact opposite of the request, on the one
   * parameter whose whole purpose is choosing between two instances.
   */
  readonly previous?: 'true' | 'false';
  /** `[1, MAX_TAIL_LINES]`, default {@link DEFAULT_TAIL_LINES}. */
  readonly tailLines?: number;
}

export function cpPodLogPath(podName: string, query: CpPodLogQueryInput = {}): string {
  const params = new URLSearchParams();
  if (query.container !== undefined) params.set('container', query.container);
  if (query.previous !== undefined) params.set('previous', query.previous);
  /*
   * ⛔ `tailLines` is ALWAYS emitted, defaulted client-side rather than left to the server.
   * The `appLogs.api.ts:130-134` convention: the pane renders a "showing N lines" line, and it can
   * only name a bound it actually sent. A server-side default is invisible to the caller, so the
   * console would have to hard-code the same number to display it — two sources for one value.
   */
  params.set('tailLines', String(query.tailLines ?? DEFAULT_TAIL_LINES));
  /*
   * ⛔ `encodeURIComponent` on `podName` — the one segment that is not a uuid.
   *
   * ⚠️ AND IT IS NOT A TRAVERSAL FENCE. `encodeURIComponent('..') === '..'` (`.` is RFC 3986
   * *unreserved*, so nothing is escaped) and `new URL()` applies dot-segment removal AFTER this
   * runs. Encoding here is about producing a well-formed URL. The actual refusal is the producer's
   * `cpPodNameSchema` at the `@Param` boundary, which requires `[a-z0-9]` at both ends and so
   * rejects `.` and `..` outright before any cluster API is reached.
   */
  return `/v1/admin/control-plane/pods/${encodeURIComponent(podName)}/logs?${params.toString()}`;
}

/**
 * One container's log, one shot, bounded.
 *
 * ⛔ The response type is `AppPodLogResponse`, IMPORTED not re-declared. The question is identical
 * to the app-scoped read — one container, one shot, bounded, `previous`, `tailLines` — and both
 * truncation rulings are the same rulings (`truncated: true` means "ask for more"; a 413 means "ask
 * for fewer"). A `cp`-named copy would be a fork with a different name, and the first producer
 * change would make the two disagree.
 */
export function getCpPodLog(
  podName: string,
  query: CpPodLogQueryInput = {},
): Promise<AppPodLogResponse> {
  return api.get<AppPodLogResponse>(cpPodLogPath(podName, query));
}
